/* eslint-disable prefer-arrow-callback */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSoilSightCurrency, useTranslation } from "utils";

import { CROP_DETAILS_CURRENCY_TOKEN, CROP_DETAILS_FIELD_LABEL_KEYS, CROP_DETAILS_FIELD_UNITS, GUIDANCE_KEY_MAP, HelpFieldKey } from "consts";

/** What each drawer field needs to show its guidance: report focus, and read its label and unit. */
export type CropDetailsFieldGuidance = {
  onFocus: (fieldKey: HelpFieldKey) => void;
  getFieldLabel: (fieldKey: HelpFieldKey) => string;
  getUnitSuffix: (fieldKey: HelpFieldKey) => string | undefined;
};

/** Focused field of the crop details drawer plus the label, unit and guidance text shown for each field. */
export const useCropDetailsGuidance = () => {
  const { t } = useTranslation("scenarioAnalysis");
  const { currencySymbol } = useSoilSightCurrency();
  const [focusedField, setFocusedField] = useState<HelpFieldKey | null>("crop");
  const guidancePanelRef = useRef<HTMLElement>(null);

  useEffect(
    function scrollGuidanceToTop() {
      if (guidancePanelRef.current) {
        guidancePanelRef.current.scrollTop = 0;
      }
    },
    [focusedField]
  );

  const getFieldLabel = useCallback((fieldKey: HelpFieldKey): string => t(CROP_DETAILS_FIELD_LABEL_KEYS[fieldKey]), [t]);

  const getUnitSuffix = useCallback(
    (fieldKey: HelpFieldKey): string | undefined => CROP_DETAILS_FIELD_UNITS[fieldKey]?.replace(CROP_DETAILS_CURRENCY_TOKEN, () => currencySymbol),
    [currencySymbol]
  );

  const guidance: CropDetailsFieldGuidance = useMemo(() => ({ onFocus: setFocusedField, getFieldLabel, getUnitSuffix }), [getFieldLabel, getUnitSuffix]);

  const unitSuffix = focusedField ? getUnitSuffix(focusedField) : undefined;
  const focusedGuidance = focusedField
    ? {
        fieldLabel: getFieldLabel(focusedField),
        description: t(`cropDetails.guidance.${GUIDANCE_KEY_MAP[focusedField]}.description`),
        source: t(`cropDetails.guidance.${GUIDANCE_KEY_MAP[focusedField]}.source`),
        unit: unitSuffix ? `(${unitSuffix})` : undefined,
      }
    : null;

  return { focusedField, focusedGuidance, guidancePanelRef, guidance };
};
